import { useEffect, useContext } from "react"
import { UserContext } from "../../../../../../context/UserContext"
import { Stack, Typography, Box } from "@mui/material"
import { FormContainer, NavButtons } from "../../../../Menu/Flow"
import CheckboxField from "../../../../../../components/CheckboxField"
import { Field } from "formik"
import employerFlowStyles from "../../styles"

export default function EligibleIndividuals(props) {
    const { mode, exitUrl, step, formik, onboarding, permissibleClassId, displayOnly } = props
    const { errors, touched, values, setFieldValue, submitCount } = formik
    const { user, getEmployer, getEmployerPlan, getEmployerPlanId, getPermissibleClass } = useContext(UserContext)

    const permissibleClass = getPermissibleClass(permissibleClassId)
    const employerPlan = getEmployerPlan(permissibleClass?.employerPlanId)

    const metalV2 = permissibleClass?.metalV2 || false

    const allowanceMethod = permissibleClass?.allowanceMethod || values?.allowanceMethod || {}
    const maxBenefit = values?.maxBenefit || {}

    const isPctOfMetal = allowanceMethod === "PCT_OF_METAL" || allowanceMethod?.PCT_OF_METAL
    const isManualFamily = (allowanceMethod === "MANUAL" || allowanceMethod?.MANUAL) && (maxBenefit.FAMILY || maxBenefit.FAMILY_AGE)

    // metalV2 PCT_OF_METAL with a dependent % > 0: the allowance covers the
    // family, so eligibility must include the family.
    const dependentPercent = values?.metalPercentDependent
    const dependentPercentIsPositive =
        dependentPercent !== null &&
        dependentPercent !== undefined &&
        dependentPercent !== "" &&
        Number(dependentPercent) > 0

    const usesLegacyMetalEligibility = employerPlan?.acaAffordabilityChoiceEnabled !== true
    const isPctOfMetalFamily = isPctOfMetal && metalV2 && usesLegacyMetalEligibility && dependentPercentIsPositive

    /**
     * NOTHING FORCES EMPLOYEE ONLY. With PCT_OF_METAL or ACA_MINIMUM, an
     * employee-only allowance can still reimburse family expenses, so the
     * employer chooses. Only a family allowance forces Family.
     */
    const forceFamily = isManualFamily || isPctOfMetalFamily

    const eligibleIndividualsOptions = [
        {
            display: "Employee and their family (spouse and dependents)",
            value: "FAMILY"
        },
        {
            display: "Employee only",
            value: "SELF"
        }
    ]

    const shouldShowErrors = submitCount > 0

    // For MANUAL FAMILY/FAMILY_AGE (or metalV2 PCT_OF_METAL with > 0 dependent %), auto-set to Employee and Family
    useEffect(() => {
        if (forceFamily) {
            setFieldValue("eligibleIndividuals", { FAMILY: true })
            setFieldValue("eligibleIndividuals_required", "yes")
        }
    }, [forceFamily, setFieldValue])

    useEffect(() => {
        if (!forceFamily) {
            const eligibleIndividualsSelection = values?.eligibleIndividuals || {}
            const hasSelection = Object.values(eligibleIndividualsSelection).some(Boolean)
            const nextRequiredValue = hasSelection ? "yes" : ""
            if ((values?.eligibleIndividuals_required || "") !== nextRequiredValue) {
                setFieldValue("eligibleIndividuals_required", nextRequiredValue)
            }
        }
    }, [forceFamily, values?.eligibleIndividuals, values?.eligibleIndividuals_required, setFieldValue])

    useEffect(() => {
        if (shouldShowErrors && errors?.eligibleIndividuals) {
            formik.setFieldTouched("eligibleIndividuals", true, false)
        }
    }, [shouldShowErrors, errors?.eligibleIndividuals])

    return (
        <Stack spacing="30px" height="100%">
            <FormContainer>
                {forceFamily && (
                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: "#f8f9fc",
                            border: "1px solid #ACBEE8",
                            borderRadius: "8px",
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
                            Family Eligibility Required
                        </Typography>
                        <Typography sx={{ fontSize: "14px" }}>
                            {isPctOfMetalFamily
                                ? "Because a dependent percentage is set, coverage must include employee and family."
                                : "Since the allowance is structured by family tier, coverage must include employee and family."}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: "#f8f9fc",
                        border: "1px solid #ACBEE8",
                        borderRadius: "8px",
                    }}
                >
                    <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
                        How are Eligible Individuals impacted
                    </Typography>
                    <Typography sx={{ fontSize: "14px", marginBottom: "8px" }}>
                        <b>Employee only:</b> Only expenses incurred by the employee are eligible for reimbursement.
                    </Typography>
                    <Typography sx={{ fontSize: "14px" }}>
                        <b>Employee and their family:</b> An expense for any family member listed on the proof of insurance is eligible for reimbursement. The monthly allowance can be applied to expenses of any family member.
                    </Typography>
                </Box>

                <Typography variant="h2" sx={employerFlowStyles.h2}>Eligible Individuals</Typography>
                <Typography>
                    Select whose expenses are eligible for reimbursement
                </Typography>
                <Stack spacing={2} sx={{ marginTop: "20px" }}>
                    {eligibleIndividualsOptions.map((option) => {
                        const isForcedSelection = forceFamily && option.value === "FAMILY"
                        const isDisabled = displayOnly || forceFamily

                        return (
                            <Box
                                key={option.value}
                                sx={{
                                    opacity: isDisabled && !isForcedSelection ? 0.5 : 1,
                                    pointerEvents: isDisabled ? "none" : "auto",
                                }}
                            >
                                <CheckboxField
                                    formik={formik}
                                    type="radio"
                                    name="eligibleIndividuals"
                                    options={[option]}
                                    displayKey="display"
                                    valueKey="value"
                                    disabled={isDisabled}
                                    showError={false}
                                />
                            </Box>
                        )
                    })}

                    {!forceFamily && shouldShowErrors && !Object.values(values?.eligibleIndividuals || {}).some(Boolean) && (
                        <div className="error-message-fields">
                            Please select at least one eligible individual option before continuing
                        </div>
                    )}

                    <Field name="eligibleIndividuals_required">
                        {({ field }) => (
                            <input
                                {...field}
                                type="hidden"
                            />
                        )}
                    </Field>
                </Stack>
            </FormContainer>
            <NavButtons mode={mode} enableSave={true} exitUrl={exitUrl} step={step} />
        </Stack>
    )
}